import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";

export default function PaymentResult() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const isCancel = window.location.pathname.includes("cancel");
  const [state, setState] = useState(isCancel ? "cancelled" : "checking");
  const [kind, setKind] = useState(null);

  useEffect(() => {
    if (isCancel || !sessionId) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setKind(data.kind);
        if (data.payment_status === "authorized" || data.payment_status === "paid") {
          setState("success");
          return;
        }
      } catch (e) {}
      if (tries++ < 8) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [sessionId, isCancel]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5">
      <div className="max-w-md w-full bg-card rounded-3xl border border-primary/5 p-10 text-center shadow-lg" data-testid="payment-result">
        {state === "checking" && (
          <>
            <Loader2 className="h-14 w-14 text-accent animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && kind === "premium" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-accent mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">You're Premium! 🎉</h1>
            <p className="text-muted-foreground mt-2">You now rank top of search, have a verified badge and pay zero service fees.</p>
            <button onClick={() => navigate("/cleaner/dashboard")} className="mt-6 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors">Go to dashboard</button>
          </>
        )}
        {state === "success" && kind === "cleaner_fee" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-accent mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">Balance cleared</h1>
            <p className="text-muted-foreground mt-2">Thanks — your account restriction has been lifted and you can accept new bookings again.</p>
            <button onClick={() => navigate("/cleaner/dashboard")} className="mt-6 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors" data-testid="fee-paid-dashboard-btn">Back to dashboard</button>
          </>
        )}
        {state === "success" && !["premium", "cleaner_fee"].includes(kind) && (
          <>
            <CheckCircle2 className="h-14 w-14 text-accent mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">Payment authorised</h1>
            <p className="text-muted-foreground mt-2">Your request has been sent. <strong>Your booking is not guaranteed until the cleaner accepts.</strong> You'll only be charged once they confirm.</p>
            <button onClick={() => navigate("/customer/bookings")} className="mt-6 px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors" data-testid="view-bookings-btn">View my bookings</button>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle className="h-14 w-14 text-muted-foreground mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">Payment cancelled</h1>
            <p className="text-muted-foreground mt-2">No charge was made. You can try booking again anytime.</p>
            <Link to="/search" className="mt-6 inline-block px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">Back to search</Link>
          </>
        )}
        {state === "timeout" && (
          <>
            <Clock className="h-14 w-14 text-muted-foreground mx-auto" />
            <h1 className="font-display text-2xl font-semibold mt-6">Still processing</h1>
            <p className="text-muted-foreground mt-2">Your payment is taking longer than expected. Check your bookings shortly.</p>
            <button onClick={() => navigate("/customer/bookings")} className="mt-6 px-6 py-3 rounded-full bg-primary text-primary-foreground font-medium">View bookings</button>
          </>
        )}
      </div>
    </div>
  );
}
